import type {UserTraits} from '@src/Tracking/catalog';
import type {
  TrackingAdapter,
  TrackingEventName,
  TrackingPayload,
} from '@src/Providers/Tracking/TrackingContext';
import {getVendorConsent} from '@src/Providers/DidomiInitProvider/isTrackingEnabled';
import apiConfig from '@src/Configs/apiConfig';

/**
 * Backend Relay adapter.
 *
 * POSTs events to trace-back's POST /api/tracking/events (or batch endpoint).
 * The backend re-fans-out S2S to Amplitude, Braze, and AppsFlyer — this is
 * the "source-of-truth" path for events backend cares about (so they get
 * validated, enriched, and stored once in the warehouse).
 *
 * Batches up to 20 events or 5s, whichever first. Failed batches are
 * persisted to localStorage and retried on the next emit.
 */

const BATCH_MAX = 20;
const BATCH_DEBOUNCE_MS = 5000;
const QUEUE_KEY = 'trace_tracking_queue_v1';

interface QueuedEvent {
  eventName: string;
  userId?: string;
  sessionId?: string;
  timestamp?: string;
  properties?: TrackingPayload;
}

const baseUrl = (): string =>
  apiConfig.tracking.relayBaseUrl || apiConfig.traceApiUrl || '';

const isConfigured = (): boolean =>
  !!baseUrl() && !!apiConfig.tracking.relayApiKey;

const loadPersistedQueue = (): QueuedEvent[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueuedEvent[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistQueue = (q: QueuedEvent[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-200)));
  } catch {
    /* swallow */
  }
};

const clearPersistedQueue = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* swallow */
  }
};

const postBatch = async (events: QueuedEvent[]): Promise<boolean> => {
  if (!isConfigured() || events.length === 0) return true;
  const url = `${baseUrl().replace(/\/$/, '')}/api/tracking/events/batch`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiConfig.tracking.relayApiKey}`,
        'x-platform': 'web',
      },
      body: JSON.stringify({
        events: events.map(e => ({
          eventName: e.eventName,
          userId: e.userId,
          sessionId: e.sessionId,
          timestamp: e.timestamp,
          properties: e.properties,
        })),
        context: {
          platform: 'web',
          page_url:
            typeof window !== 'undefined' ? window.location.href : undefined,
        },
      }),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
};

const postIdentify = async (
  userId: string,
  traits?: UserTraits,
): Promise<boolean> => {
  if (!isConfigured()) return true;
  const url = `${baseUrl().replace(/\/$/, '')}/api/tracking/identify`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiConfig.tracking.relayApiKey}`,
        'x-platform': 'web',
      },
      body: JSON.stringify({
        userId,
        properties: traits ?? {},
        context: {platform: 'web'},
      }),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
};

export const createBackendRelayAdapter = (): TrackingAdapter => {
  let buffer: QueuedEvent[] = loadPersistedQueue();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flushBuffer = async (): Promise<void> => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (buffer.length === 0) return;
    const toSend = buffer.splice(0, buffer.length);
    persistQueue(buffer);
    const ok = await postBatch(toSend);
    if (!ok) {
      // re-queue failed batch for next attempt
      buffer = [...toSend, ...buffer];
      persistQueue(buffer);
    } else {
      clearPersistedQueue();
      if (buffer.length > 0) persistQueue(buffer);
    }
  };

  const scheduleFlush = () => {
    if (timer) return;
    timer = setTimeout(() => {
      void flushBuffer();
    }, BATCH_DEBOUNCE_MS);
  };

  // Flush on tab hide / unload so we don't drop events.
  if (typeof window !== 'undefined') {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flushBuffer();
    });
    window.addEventListener('pagehide', () => {
      void flushBuffer();
    });
  }

  return {
    name: 'backendRelay',
    isEnabled: () => isConfigured() && getVendorConsent().backendRelay,

    track: (event: TrackingEventName, props?: TrackingPayload) => {
      if (typeof event !== 'string') return;
      buffer.push({
        eventName: event,
        userId: (props?.user_id as string | undefined) ?? undefined,
        sessionId: (props?.session_id as string | undefined) ?? undefined,
        timestamp: (props?.event_time as string | undefined) ?? undefined,
        properties: props,
      });
      persistQueue(buffer);
      if (buffer.length >= BATCH_MAX) {
        void flushBuffer();
      } else {
        scheduleFlush();
      }
    },

    identify: async (userId, traits) => {
      await postIdentify(userId, traits);
    },

    flush: flushBuffer,
  };
};
